import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
gsap.registerPlugin(ScrollTrigger)

export class LogosWheel {
  constructor() {
    this.isInitialized = false
    this.scrollTrigger = null
    this.timeline = null
    this.resizeHandler = null
    this.wheelCards = gsap.utils.toArray(".wheel_card_wrap")
    this.currentBreakpoint = null
  }

  init() {
    if (this.isInitialized) return
    this.initAnimation()
    this.bindResize()
    this.isInitialized = true
  }

  initAnimation() {
    this.cleanup()

    const isMobile = window.innerWidth <= 768
    const newBreakpoint = isMobile ? 'mobile' : 'desktop'

    if (this.currentBreakpoint === newBreakpoint && this.timeline) return
    this.currentBreakpoint = newBreakpoint

    const wheelPath = document.querySelector("#wheelPath")
    const wheelSection = document.querySelector("#wheelSection")

    if (!wheelPath || !wheelSection) {
      console.warn("wheelPath or wheelSection not found")
      return
    }

    // 🚀 Defer ALL layout reads to next frame
    requestAnimationFrame(() => {

      const pathBBox = wheelPath.getBBox()
      const svgElement = wheelPath.ownerSVGElement
      const svgRect = svgElement.getBoundingClientRect()
      const containerRect = wheelSection.getBoundingClientRect()

      const viewBox = svgElement.viewBox.baseVal
      const scaleX = svgRect.width / viewBox.width
      const scaleY = svgRect.height / viewBox.height

      const actualRadius = (pathBBox.width / 2) * scaleX
      const centerX = (pathBBox.x + pathBBox.width / 2) * scaleX + (svgRect.left - containerRect.left)
      const centerY = (pathBBox.y + pathBBox.height / 2) * scaleY + (svgRect.top - containerRect.top)

      const cardCount = this.wheelCards.length
      const startAngleOffset = -Math.PI / 2

      const getPosition = (cardIndex, rotationOffset) => {
        const angle = (cardIndex / cardCount) * Math.PI * 2 + startAngleOffset + rotationOffset
        return {
          x: Math.cos(angle) * actualRadius + centerX,
          y: Math.sin(angle) * actualRadius + centerY
        }
      }

      // 🚀 Defer ALL writes to the next RAF to avoid read-write-read-write thrashing
      requestAnimationFrame(() => {

        gsap.set(this.wheelCards, {
          position: "absolute",
          left: 0,
          top: 0,
          xPercent: -50,
          yPercent: -50,
          rotation: 0,
          force3D: true
        })

        // set initial positions
        this.wheelCards.forEach((card, i) => {
          const startPos = getPosition(i, 0)
          gsap.set(card, {
            x: startPos.x,
            y: startPos.y,
            force3D: true
          })
        })

        // Build timeline AFTER layout is stable
        this.timeline = gsap.timeline({ defaults: { ease: "none" } })

        const rotationSteps = 36

        for (let step = 1; step <= rotationSteps; step++) {
          const rotationAmount = (step / rotationSteps) * Math.PI * 2

          this.timeline.to(this.wheelCards, {
            duration: 1 / rotationSteps,
            x: i => getPosition(i, rotationAmount).x,
            y: i => getPosition(i, rotationAmount).y,
            force3D: true
          })
        }

        this.scrollTrigger = ScrollTrigger.create({
          trigger: "#wheelSection",
          start: "top 50%",
          end: "bottom top",
          scrub: 2,
          animation: this.timeline,
          fastScrollEnd: true
        })
      })
    })
  }


  bindResize() {
    this.resizeHandler = this.debounceResize(() => {
      const isMobile = window.innerWidth <= 768
      const newBreakpoint = isMobile ? 'mobile' : 'desktop'

      if (this.currentBreakpoint !== newBreakpoint) {
        this.initAnimation()
      } else {
        ScrollTrigger.refresh()
      }
    }, 250)

    window.addEventListener("resize", this.resizeHandler, { passive: true })
  }

  debounceResize(fn, ms) {
    let last = innerWidth
    let timer
    return function (...args) {
      clearTimeout(timer)
      timer = setTimeout(() => {
        if (innerWidth !== last) {
          last = innerWidth
          fn.apply(this, args)
        }
      }, ms)
    }
  }

  cleanup() {
    if (this.scrollTrigger) {
      this.scrollTrigger.kill()
      this.scrollTrigger = null
    }
    if (this.timeline) {
      this.timeline.kill()
      this.timeline = null
    }

    gsap.set(this.wheelCards, { clearProps: "all" })
  }

  destroy() {
    this.cleanup()

    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler)
      this.resizeHandler = null
    }

    this.isInitialized = false
    this.currentBreakpoint = null
  }
}